import { SITE_URL, CONTACT_EMAILS, REGIONS } from '@/config'
import { DEFAULT_PAGE_TITLE } from '@/constants'

const BRAND = 'КРОН'

export function buildAbsoluteUrl(path = '/') {
  const origin = SITE_URL.replace(/\/$/, '')
  if (!path || path === '/') return `${origin}/`
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${buildAbsoluteUrl('/')}#organization`,
    name: BRAND,
    legalName: 'ООО «КРОН»',
    url: buildAbsoluteUrl('/'),
    email: CONTACT_EMAILS.MAIN,
    telephone: REGIONS.MOSCOW.phone,
    logo: buildAbsoluteUrl('/big-logo.webp'),
    sameAs: [
      'https://apps.apple.com/ru/app/%D0%BA%D1%80%D0%BE%D0%BD-%D0%B8%D0%BD%D1%82%D0%B5%D1%80%D0%BD%D0%B5%D1%82-%D0%BC%D0%B0%D0%B3%D0%B0%D0%B7%D0%B8%D0%BD/id1611541742',
      'https://www.rustore.ru/catalog/app/ru.tovarikron.android'
    ],
    address: [
      {
        '@type': 'PostalAddress',
        addressLocality: 'Москва',
        streetAddress: REGIONS.MOSCOW.address,
        addressCountry: 'RU'
      },
      {
        '@type': 'PostalAddress',
        addressLocality: 'Ставропольский край',
        streetAddress: REGIONS.STAVROPOL.address,
        addressCountry: 'RU'
      }
    ]
  }
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${buildAbsoluteUrl('/')}#website`,
    name: DEFAULT_PAGE_TITLE,
    url: buildAbsoluteUrl('/'),
    inLanguage: 'ru-RU',
    publisher: { '@id': `${buildAbsoluteUrl('/')}#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${buildAbsoluteUrl('/search')}/{search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  }
}

export function buildBreadcrumbSchema(items) {
  if (!items?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? buildAbsoluteUrl(item.url) : undefined
    }))
  }
}

export function buildProductSchema(product, categorySlug) {
  if (!product?.name || !categorySlug) return null

  const images = (product.images || [])
    .map((image) => image?.url)
    .filter(Boolean)

  const availabilityMap = {
    IN_STOCK: 'https://schema.org/InStock',
    AVAILABLE: 'https://schema.org/InStock',
    ON_ORDER: 'https://schema.org/PreOrder',
    IN_STOCK_SOON: 'https://schema.org/PreOrder',
    OUT_OF_STOCK: 'https://schema.org/OutOfStock'
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: stripHtml(product.description) || product.name,
    sku: product.id,
    url: buildAbsoluteUrl(`/category/${categorySlug}/product/${product.slug}`),
    brand: {
      '@type': 'Brand',
      name: BRAND
    },
    offers: {
      '@type': 'Offer',
      url: buildAbsoluteUrl(`/category/${categorySlug}/product/${product.slug}`),
      priceCurrency: 'RUB',
      price: product.price,
      availability: availabilityMap[product.stockAvailabilityStatus] || 'https://schema.org/InStock',
      seller: { '@id': `${buildAbsoluteUrl('/')}#organization` }
    }
  }

  if (images.length) {
    schema.image = images
  }

  return schema
}

export function buildFaqSchema(faqItems) {
  if (!faqItems?.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  }
}

function stripHtml(html) {
  if (!html || typeof html !== 'string') return ''
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
